import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Plus, Clock, CheckCircle, AlertCircle, AlertTriangle, Calendar } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPDDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0,
    dueToday: 0,
    dueSoon: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/spd/orders?limit=10`);
      if (response.ok) {
        const data = await response.json();
        const orders = data.orders || [];
        setRecentOrders(orders.slice(0, 5));
        
        // Calculate due date stats
        let overdue = 0, dueToday = 0, dueSoon = 0;
        const now = new Date();
        
        orders.forEach(order => {
          if (order.scheduled_datetime && order.status !== 'Completed') {
            const dueDate = new Date(order.scheduled_datetime);
            const diffMs = dueDate - now;
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            
            if (diffMs < 0) overdue++;
            else if (diffDays === 0) dueToday++;
            else if (diffDays <= 3) dueSoon++;
          }
        });
        
        setStats({
          total: data.total || 0,
          pending: data.pending || 0,
          inProgress: data.in_progress || 0,
          completed: data.completed || 0,
          overdue,
          dueToday,
          dueSoon
        });
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get due date status for an order
  const getDueDateStatus = (order) => {
    if (!order.scheduled_datetime || order.status === 'Completed') return null;
    
    const now = new Date();
    const dueDate = new Date(order.scheduled_datetime);
    const diffMs = dueDate - now;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMs < 0) {
      return { label: "OVERDUE", color: "bg-red-500/20 text-red-400 border-red-500/50" };
    } else if (diffDays === 0) {
      return { label: "DUE TODAY", color: "bg-orange-500/20 text-orange-400 border-orange-500/50" };
    } else if (diffDays <= 3) {
      return { label: "DUE SOON", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50" };
    }
    return null;
  };

  const statCards = [
    { title: "Total Orders", value: stats.total, icon: FileText, color: "blue" },
    { title: "Pending", value: stats.pending, icon: Clock, color: "yellow" },
    { title: "In Progress", value: stats.inProgress, icon: AlertCircle, color: "orange" },
    { title: "Completed", value: stats.completed, icon: CheckCircle, color: "green" },
  ];

  const alertCards = [
    { title: "Overdue", value: stats.overdue, icon: AlertTriangle, color: "red", bgClass: "bg-red-500/10 border-red-500/50" },
    { title: "Due Today", value: stats.dueToday, icon: Clock, color: "orange", bgClass: "bg-orange-500/10 border-orange-500/50" },
    { title: "Due Soon", value: stats.dueSoon, icon: Calendar, color: "yellow", bgClass: "bg-yellow-500/10 border-yellow-500/50" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-blue-400 uppercase tracking-wider">
            SPD Dashboard
          </h1>
          <p className="text-gray-400 mt-1">Standard Production Data Management</p>
        </div>
        <Button
          onClick={() => navigate("/spd/create-order")}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold uppercase tracking-wider shadow-lg"
          data-testid="create-new-order-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create New Order
        </Button>
      </div>

      {/* Due Date Alerts */}
      {(stats.overdue > 0 || stats.dueToday > 0 || stats.dueSoon > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {alertCards.map((alert) => {
            if (alert.value === 0) return null;
            const Icon = alert.icon;
            const colorClasses = {
              red: "text-red-400",
              orange: "text-orange-400",
              yellow: "text-yellow-400",
            };
            return (
              <Card key={alert.title} className={`${alert.bgClass} border-2 shadow-lg animate-pulse`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Icon className={`h-8 w-8 ${colorClasses[alert.color]}`} />
                    <div>
                      <p className={`text-2xl font-black ${colorClasses[alert.color]}`}>
                        {alert.value} {alert.title}
                      </p>
                      <p className="text-gray-400 text-sm">Requires attention</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          const colorClasses = {
            blue: "border-blue-500/40 text-blue-400",
            yellow: "border-yellow-500/40 text-yellow-400",
            orange: "border-orange-500/40 text-orange-400",
            green: "border-emerald-500/40 text-emerald-400",
          };
          return (
            <Card key={stat.title} className={`bg-black/80 border-2 ${colorClasses[stat.color]} shadow-lg`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm uppercase tracking-wider">{stat.title}</p>
                    <p className={`text-3xl font-black mt-1 ${colorClasses[stat.color].split(' ')[1]}`}>
                      {loading ? "-" : stat.value}
                    </p>
                  </div>
                  <Icon className={`h-10 w-10 ${colorClasses[stat.color].split(' ')[1]} opacity-50`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Orders */}
      <Card className="bg-black/80 border-2 border-blue-500/40 shadow-lg">
        <CardHeader className="border-b border-blue-500/30">
          <CardTitle className="text-xl font-bold text-blue-400 uppercase tracking-wider">
            Recent Orders
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center text-gray-400">Loading...</div>
          ) : recentOrders.length === 0 ? (
            <div className="p-6 text-center text-gray-400">
              No orders yet. Create your first order to get started.
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-blue-500/30">
                  <th className="text-left p-4 text-gray-400 font-bold uppercase text-sm">Item Name</th>
                  <th className="text-left p-4 text-gray-400 font-bold uppercase text-sm">Company</th>
                  <th className="text-left p-4 text-gray-400 font-bold uppercase text-sm">Total Qty</th>
                  <th className="text-left p-4 text-gray-400 font-bold uppercase text-sm">Status</th>
                  <th className="text-left p-4 text-gray-400 font-bold uppercase text-sm">Due Date</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order) => {
                  const dueStatus = getDueDateStatus(order);
                  return (
                    <tr key={order._id} className="border-b border-blue-500/20 hover:bg-blue-500/10 transition-colors">
                      <td className="p-4 text-white font-medium">{order.item_name}</td>
                      <td className="p-4 text-gray-300">{order.company_name || "-"}</td>
                      <td className="p-4 text-gray-300">{order.total_quantity}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                            order.status === 'Pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            order.status === 'In Progress' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-emerald-500/20 text-emerald-400'
                          }`}>
                            {order.status}
                          </span>
                          {dueStatus && (
                            <span className={`px-2 py-1 rounded text-xs font-bold uppercase border ${dueStatus.color}`}>
                              {dueStatus.label}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-gray-400">
                        {order.scheduled_datetime 
                          ? new Date(order.scheduled_datetime).toLocaleDateString() 
                          : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SPDDashboard;
